import { useState, useEffect } from "react";
import { Menu, X, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SITE } from "@/lib/siteData";

const NAV = [
  { label: "Services", href: "#services" },
  { label: "À propos", href: "#about" },
  { label: "Rendez-vous", href: "#appointment" },
  { label: "Contact", href: "#contact" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (href) => {
    setOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/90 backdrop-blur-xl border-b border-slate-200 shadow-sm" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          <a href="#top" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); }} className="flex items-center gap-3" data-testid="site-logo-link">
            <img
              src={SITE.logo.light}
              alt="Sphère Auto"
              className="h-12 lg:h-14 w-auto"
            />
            <div className="hidden sm:block leading-tight">
              <div className={`font-heading font-extrabold text-lg lg:text-xl tracking-tight transition-colors ${scrolled ? "text-slate-900" : "text-white"}`}>SPHÈRE AUTO</div>
              <div className={`text-xs -mt-0.5 transition-colors ${scrolled ? "text-slate-500" : "text-white/70"}`}>Loos-en-Gohelle • 62750</div>
            </div>
          </a>

          <nav className="hidden lg:flex items-center gap-1">
            {NAV.map((n) => (
              <a
                key={n.href}
                href={n.href}
                onClick={(e) => { e.preventDefault(); handleNav(n.href); }}
                className="px-4 py-2 rounded-md text-sm font-medium text-slate-700 hover:text-blue-600 hover:bg-slate-50 transition-colors"
                data-testid={`nav-link-${n.href.replace('#', '')}`}
              >
                {n.label}
              </a>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <a
              href={SITE.phoneTel}
              className="flex items-center gap-2 text-sm font-semibold text-slate-700 hover:text-blue-600"
              data-testid="header-phone-link"
            >
              <Phone className="w-4 h-4" />
              {SITE.phoneDisplay}
            </a>
            <Button
              onClick={() => handleNav("#appointment")}
              className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold"
              data-testid="header-rdv-button"
            >
              Prendre RDV
            </Button>
          </div>

          <button
            className={`lg:hidden p-2 transition-colors ${scrolled ? "text-slate-700" : "text-white"}`}
            onClick={() => setOpen(!open)}
            aria-label="Menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-slate-200 shadow-lg" data-testid="mobile-menu">
          <div className="px-4 py-4 space-y-1">
            {NAV.map((n) => (
              <a
                key={n.href}
                href={n.href}
                onClick={(e) => { e.preventDefault(); handleNav(n.href); }}
                className="block px-3 py-3 rounded-md text-base font-medium text-slate-700 hover:bg-slate-100"
                data-testid={`mobile-nav-link-${n.href.replace('#', '')}`}
              >
                {n.label}
              </a>
            ))}
            <a
              href={SITE.phoneTel}
              className="flex items-center gap-2 px-3 py-3 text-base font-semibold text-blue-600"
              data-testid="mobile-phone-link"
            >
              <Phone className="w-5 h-5" /> {SITE.phoneDisplay}
            </a>
            <Button
              onClick={() => handleNav("#appointment")}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="mobile-rdv-button"
            >
              Prendre RDV
            </Button>
          </div>
        </div>
      )}
    </header>
  );
}
